import { useEffect, useState } from 'react';
import { ExpansionPanel, EditableDenseTable } from '@/components/common';
import { useCompanyAnnualStore } from '@/store/audit/companyAnnualStore';
import type { AuditStaff, CompanyAnnual } from '@/services/accountService/companyAnnualService';
import { queryUsersByDeptIds } from '@/services/manageService/userService';
import type { UserInfo } from '@/store/admin/userInfo';

type Props = {
  annualId: number;
  onChange?: (patch: Partial<CompanyAnnual>) => void;
  disableAccordion?: boolean;
};

export default function EpAuditStaffs({ annualId, onChange, disableAccordion = false }: Props) {
  const getAuditStaffsInfo = useCompanyAnnualStore(s => s.getAuditStaffsInfo);
  const [rows, setRows] = useState<AuditStaff[]>([]);
  const [deptUsers, setDeptUsers] = useState<UserInfo[]>([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [list, users] = await Promise.all([
        getAuditStaffsInfo(annualId),
        queryUsersByDeptIds(2001).catch(() => []) as any,
      ]);
      if (!mounted) return;
      setRows(Array.isArray(list) ? list : []);
      setDeptUsers(Array.isArray((users as any)?.data ?? users) ? ((users as any).data ?? users) : []);
    })();
    return () => { mounted = false; };
  }, [annualId, getAuditStaffsInfo]);

  return (
    <ExpansionPanel 
      title="Audit Staffs (審計人員)"
      expanded={disableAccordion ? true : undefined}
      onChange={disableAccordion ? () => {} : undefined}
    >
      <EditableDenseTable
        label=""
        head={['Staff Name', 'Post', 'Initial', 'Role']}
        rows={(rows || []).map(s => {
          const u = deptUsers.find(x => x.userId === s.userId);
          const initials = `${(u?.firstName || '').charAt(0)}${(u?.lastName || '').charAt(0)}`;
          return [s.userId || '', u?.post || '', initials, s.roleType != null ? String(s.roleType) : ''];
        })}
        columnKeys={['userId', 'post', 'initial', 'role']}
        columnConfigs={[
          {
            type: 'select',
            options: ({ rowIndex, rows }) => {
              const selectedFromTable = new Set<number>(rows.map(r => Number(r?.[0] ?? 0)).filter(v => !!v));
              const selectedFromForm = new Set<number>((rows || []).map(r => Number(r?.[0] || 0)).filter(v => !!v));
              const selected = new Set<number>();
              selectedFromTable.forEach(v => selected.add(v));
              selectedFromForm.forEach(v => selected.add(v));
              const currentVal = Number(rows[rowIndex]?.[0] ?? 0);
              return deptUsers
                .filter(u => u.userId === currentVal || !selected.has(u.userId))
                .map(u => ({ label: `${u.firstName || ''} ${u.lastName || ''}`.trim(), value: u.userId }));
            },
            componentProps: { placeholder: 'Select staff' },
          },
          { type: 'text', componentProps: { display: true } },
          { type: 'text', componentProps: { display: true } },
          { type: 'select', options: [ { label: 'Preparer', value: 1 }, { label: 'Reviewer', value: 2 }, { label: 'Partner', value: 3 } ] },
        ]}
        onChangeCellApply={(rowIndex, colIndex, value, currentRow) => {
          if (colIndex === 0) {
            const uid = Number(value || 0);
            const u = deptUsers.find(x => x.userId === uid);
            const initials = `${(u?.firstName || '').charAt(0)}${(u?.lastName || '').charAt(0)}`;
            return { 1: u?.post || '', 2: initials };
          }
        }}
        onRowsChangeObjects={(list) => {
          const all = (list as any[])
            .map(o => ({ userId: Number(o.userId ?? 0) || 0, roleType: Number(o.role ?? 0) || 0 })) as AuditStaff[];
          // Keep all rows (including placeholders) locally so newly added row stays visible
          setRows(all);
          // Only propagate valid entries (userId > 0) to parent form state
          const valid = all.filter(s => Number(s.userId || 0) > 0);
          onChange?.({ auditStaffs: valid } as Partial<CompanyAnnual>);
        }}
      />
    </ExpansionPanel>
  );
}
